import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { 
  GraduationCap, 
  Briefcase, 
  BookOpen, 
  Globe, 
  Monitor, 
  Car, 
  User, 
  Heart, 
  Users, 
  Award,
  FileText,
  X,
  MapPin,
  Calendar,
  Check,
  Printer
} from 'lucide-react';
import AddCandidateToSelectionModal from '../components/Employer/AddCandidateToSelectionModal';
import ViewMotivationLetterModal from '../components/Employer/ViewMotivationLetterModal';
import SuccessNotification from '../components/Common/SuccessNotification';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import { invitationLetterService, InvitationLetter } from '../services/invitationLetterService';
import { jobInvitationService } from '../services/jobInvitationService';
import jobService from '../services/jobService';
import './CandidateDetailPage.scss';

const PROFICIENCY_TO_PCT: Record<string, string> = {
  // Lithuanian
  'Puikiai': '100%', 'Gerai': '80%', 'Vidutiniškai': '60%', 'Patenkinamai': '40%', 'Silpnai': '20%',
  // English
  'Excellent': '100%', 'Good': '80%', 'Intermediate': '60%', 'Basic': '40%', 'Beginner': '20%',
  // Russian
  'Отлично': '100%', 'Хорошо': '80%', 'Средне': '60%', 'Удовлетворительно': '40%', 'Слабо': '20%',
  // German
  'Ausgezeichnet': '100%', 'Gut': '80%', 'Mittelmäßig': '60%', 'Grundkenntnisse': '40%', 'Anfänger': '20%',
  // Polish
  'Doskonale': '100%', 'Dobrze': '80%', 'Średnio': '60%', 'Podstawowo': '40%', 'Słabo': '20%',
};

const levelToPct = (level: string | undefined): string => {
  if (!level) return '0%';
  return PROFICIENCY_TO_PCT[level] || level;
};

interface Candidate {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  location?: string;
  description: string;
  birth_date?: string;
  gender?: string;
  profile_image_url?: string;
  work_experience: any[];
  education: any[];
  additional_training?: any[];
  language_skills: any[];
  computer_skills: any;
  driving_skills: any[];
  driving_experience?: string;
  job_preferences: any;
  personal_qualities?: string;
  hobbies?: string;
  organization_memberships?: any[];
  recommendations?: any[];
  native_language?: string;
  use_own_car?: boolean;
  created_at: string;
  updated_at: string;
  cv_visibility_restriction?: boolean;
}

const CandidateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const fromRect = (location.state as any)?.fromRect as { x: number; y: number; width: number; height: number } | undefined;
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddToSelectionModal, setShowAddToSelectionModal] = useState(false);
  const [showMotivationLetterModal, setShowMotivationLetterModal] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [invitationLetters, setInvitationLetters] = useState<InvitationLetter[]>([]);
  const [selectedLetterId, setSelectedLetterId] = useState<number | null>(null);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [employerJobs, setEmployerJobs] = useState<Array<{ id: number; title: string }>>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const { showToast } = useToast();

  // Extract job_id from URL returnUrl if coming from a specific job's applications page
  const jobIdFromUrl = (() => {
    const sp = new URLSearchParams(window.location.search);
    const ru = sp.get('returnUrl') || '';
    const m = ru.match(/\/jobs\/(\d+)\/applications/);
    return m ? parseInt(m[1]) : null;
  })();

  const handleClose = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const searchParams = new URLSearchParams(window.location.search);
    const mode = searchParams.get('mode');
    const returnUrl = searchParams.get('returnUrl');
    
    if (returnUrl) {
      // Decode and navigate to the return URL (preserved search state)
      navigate(decodeURIComponent(returnUrl));
    } else if (mode === 'employer') {
      navigate('/employer/candidates');
    } else {
      navigate(-1);
    }
  }, [navigate]);

  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  useEffect(() => {
    fetchCandidate();
    if (user?.user_type === 'employer') {
      loadInvitationLetters();
      loadEmployerJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const loadInvitationLetters = async () => {
    try {
      const letters = await invitationLetterService.getAll();
      setInvitationLetters(letters);
      if (letters.length > 0) setSelectedLetterId(letters[0].id!);
    } catch (error) {
      console.error('Error loading invitation letters:', error);
    }
  };

  const loadEmployerJobs = async () => {
    try {
      const jobs = await jobService.getEmployerJobs();
      setEmployerJobs(jobs);
      // Auto-select the first job so the field is never blank
      if (jobs.length > 0 && !jobIdFromUrl) {
        setSelectedJobId(jobs[0].id);
      }
    } catch (error) {
      console.error('Error loading employer jobs:', error);
    }
  };

  const handleSendInvitation = () => {
    if (!selectedLetterId) { showToast('Prašome pasirinkti laišką', 'warning'); return; }
    // If no job context from URL, require a job selection from the dropdown
    if (!jobIdFromUrl && !selectedJobId && employerJobs.length > 0) {
      showToast('Prašome pasirinkti darbo skelbimą', 'warning');
      return;
    }
    if (!candidate) return;
    setShowConfirmDialog(true);
  };

  const confirmSendInvitation = async () => {
    setShowConfirmDialog(false);
    if (!candidate || !selectedLetterId) return;
    // Resolve job_id: prefer URL context, then user's manual selection
    const resolvedJobId = jobIdFromUrl ?? selectedJobId ?? undefined;
    try {
      setSendingInvitation(true);
      await jobInvitationService.send({
        candidate_id: candidate.user_id,
        cv_id: candidate.id,
        invitation_letter_id: selectedLetterId,
        ...(resolvedJobId ? { job_id: resolvedJobId } : {})
      });
      setShowSuccessNotification(true);
    } catch (error) {
      console.error('Error sending invitation:', error);
      showToast('Nepavyko išsiųsti kvietimo. Bandykite dar kartą.', 'error');
    } finally {
      setSendingInvitation(false);
    }
  };

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [handleClose]);

  const fetchCandidate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/cv/${id}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const result = await response.json();
        setCandidate(result.data);
      }
    } catch (error) {
      console.error('Error fetching candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.');
  };

  const formatDateShort = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const getAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const getCity = (address: string, location?: string) => {
    if (location) return location;
    if (!address) return '';
    const parts = address.split(',');
    return parts[parts.length - 1]?.trim() || address;
  };

  const calculateDuration = (startDate?: string, endDate?: string) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years > 0 && remainingMonths > 0) return `(${years} m. ir ${remainingMonths} mėn.)`;
    if (years > 0) return `(${years} m.)`;
    if (remainingMonths > 0) return `(${remainingMonths} mėn.)`;
    return '';
  };

  const handlePrint = () => window.print();

  // Helper to get full profile image URL
  const getProfileImageUrl = (profileImageUrl?: string) => {
    if (!profileImageUrl) return null;
    if (profileImageUrl.startsWith('http')) return profileImageUrl;
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}${profileImageUrl}`;
  };

  // Helper to check if array has data
  const hasData = (arr: any[] | undefined | null) => arr && Array.isArray(arr) && arr.length > 0;
  const hasObjectData = (obj: any) => obj && typeof obj === 'object' && Object.keys(obj).some(k => obj[k]?.length > 0);

  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return ReactDOM.createPortal(
      <div className="candidate-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Kraunama...</p>
        </div>
      </div>,
      document.body
    );
  }

  if (!candidate) {
    return ReactDOM.createPortal(
      <div className="candidate-detail-page">
        <div className="error-container">
          <p>Kandidatas nerastas</p>
          <button onClick={() => handleClose()} className="btn-back">Grįžti</button>
        </div>
      </div>,
      document.body
    );
  }

  const age = getAge(candidate.birth_date);
  const city = getCity(candidate.address, candidate.location);

  const originStyle = fromRect ? {
    '--origin-x': `${fromRect.x + fromRect.width / 2}px`,
    '--origin-y': `${fromRect.y + fromRect.height / 2}px`,
  } as React.CSSProperties : undefined;

  const modalContent = (
    <div className={`candidate-detail-page ${fromRect ? 'animate-in' : ''}`} style={originStyle} onClick={handleOverlayClick}>
      <div className="cv-modal">
        {/* Close & Print Buttons */}
        <div className="modal-actions">
          <button className="btn-print" onClick={handlePrint} title="Spausdinti"><Printer size={18} /></button>
          <button className="btn-close" onClick={handleClose} title="Uždaryti"><X size={20} /></button>
        </div>

        {/* ==================== HEADER ==================== */}
        <header className="cv-header">
          <div className="header-main">
            <div className="header-photo">
              <img 
                src={getProfileImageUrl(candidate.profile_image_url) || `https://ui-avatars.com/api/?name=${candidate.first_name}+${candidate.last_name}&background=5a5a5a&color=fff&size=200`}
                alt={`${candidate.first_name} ${candidate.last_name}`}
                onError={(e) => {
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${candidate.first_name}+${candidate.last_name}&background=5a5a5a&color=fff&size=200`;
                }}
              />
            </div>
            <div className="header-info">
              <h1>{candidate.first_name} {candidate.last_name.charAt(0)}.</h1>
              {candidate.description && <p className="description">{candidate.description}</p>}
            </div>
          </div>
          <div className="header-meta">
            {candidate.birth_date && (
              <div className="meta-item">
                <Calendar size={16} />
                <span>{formatDate(candidate.birth_date)}</span>
                {age && <span className="meta-secondary">({age} metai)</span>}
              </div>
            )}
            {city && (
              <div className="meta-item">
                <MapPin size={16} />
                <span>{city}</span>
              </div>
            )}
          </div>
        </header>

        {/* ==================== CV SECTIONS ==================== */}
        <div className="cv-body">
          
          {/* 1. IŠSILAVINIMAS (Education) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Išsilavinimas</span>
              <div className="label-icon"><GraduationCap size={22} /></div>
            </div>
            <div className="row-content">
              {hasData(candidate.education) ? (
                candidate.education.map((edu: any, index: number) => (
                  <div key={index} className="education-item">
                    <div className="edu-period">{edu.startYear || edu.startDate} - {edu.endYear || edu.endDate || 'dabar'}</div>
                    <div className="edu-details">
                      <div className="edu-institution">{edu.institution}</div>
                      {edu.degree && <div className="edu-degree">{edu.degree}</div>}
                      {edu.field && <div className="edu-field">{edu.field}</div>}
                      {edu.specialization && <div className="edu-field">{edu.specialization}</div>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 2. DARBO PATIRTIS (Work Experience) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Darbo patirtis</span>
              <div className="label-icon"><Briefcase size={22} /></div>
            </div>
            <div className="row-content experience-content">
              {hasData(candidate.work_experience) ? (
                candidate.work_experience.map((exp: any, index: number) => (
                  <div key={index} className="experience-item">
                    <div className="exp-period">
                      <div className="exp-dates">{formatDateShort(exp.startDate)} - {exp.endDate ? formatDateShort(exp.endDate) : 'dabar'}</div>
                      <div className="exp-duration">{calculateDuration(exp.startDate, exp.endDate)}</div>
                    </div>
                    <div className="exp-company">
                      {exp.companyName && <div className="company-name">{exp.companyName}</div>}
                      {exp.website && <div className="company-website">{exp.website}</div>}
                      {exp.companyWebsite && <div className="company-website">{exp.companyWebsite}</div>}
                      {(exp.responsibilities || exp.description) && (
                        <div className="exp-duties">
                          <strong>Darbo pobūdis:</strong>
                          {Array.isArray(exp.responsibilities) ? (
                            <ul>{exp.responsibilities.map((item: string, i: number) => <li key={i}>{item}</li>)}</ul>
                          ) : (
                            <p>{exp.description || exp.responsibilities}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="exp-position">
                      {exp.position && (
                        <>
                          <strong>Pareigos:</strong>
                          <div className="position-title">{exp.position}</div>
                        </>
                      )}
                      {exp.achievements && (
                        <div className="exp-achievements">
                          <strong>Pasiekimai:</strong>
                          {Array.isArray(exp.achievements) ? (
                            <ul>{exp.achievements.map((item: string, i: number) => <li key={i}>{item}</li>)}</ul>
                          ) : (
                            <p>{exp.achievements}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 3. PAPILDOMI MOKYMAI (Additional Training) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Papildomi mokymai, seminarai, kursai</span>
              <div className="label-icon"><BookOpen size={22} /></div>
            </div>
            <div className="row-content">
              {hasData(candidate.additional_training) ? (
                candidate.additional_training!.map((course: any, index: number) => (
                  <div key={index} className="training-item">
                    <div className="training-period">
                      <div className="training-year">{course.year}</div>
                      {course.duration && <div className="training-duration">(trukmė {course.duration})</div>}
                    </div>
                    <div className="training-details">
                      <div className="training-org">{course.organization || course.provider}</div>
                      {(course.title || course.courseName) && <div className="training-title">"{course.title || course.courseName}"</div>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 4. KALBŲ ĮGŪDŽIAI (Language Skills) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Kalbų įgūdžiai</span>
              <div className="label-icon"><Globe size={22} /></div>
            </div>
            <div className="row-content">
              <div className="languages-table">
                <div className="lang-header">
                  <div className="lang-name-col"></div>
                  <div className="lang-skill-col">Supratimas</div>
                  <div className="lang-skill-col">Kalbėjimas</div>
                  <div className="lang-skill-col">Rašymas</div>
                </div>
                {candidate.native_language && (
                  <div className="lang-row native">
                    <div className="lang-name-col">{candidate.native_language} <span className="native-badge">(gimtoji)</span></div>
                    <div className="lang-skill-col"><div className="skill-bar full"></div></div>
                    <div className="lang-skill-col"><div className="skill-bar full"></div></div>
                    <div className="lang-skill-col"><div className="skill-bar full"></div></div>
                  </div>
                )}
                {hasData(candidate.language_skills) ? (
                  candidate.language_skills.map((lang: any, index: number) => (
                    <div key={index} className="lang-row">
                      <div className="lang-name-col">{lang.language}</div>
                      <div className="lang-skill-col">
                        <div className="skill-bar" style={{ '--fill': levelToPct(lang.understanding) } as React.CSSProperties}></div>
                      </div>
                      <div className="lang-skill-col">
                        <div className="skill-bar" style={{ '--fill': levelToPct(lang.speaking) } as React.CSSProperties}></div>
                      </div>
                      <div className="lang-skill-col">
                        <div className="skill-bar" style={{ '--fill': levelToPct(lang.writing) } as React.CSSProperties}></div>
                      </div>
                    </div>
                  ))
                ) : !candidate.native_language ? (
                  <div className="empty-data">Nėra duomenų</div>
                ) : null}
              </div>
            </div>
          </div>

          {/* 5. DARBO KOMPIUTERIU ĮGŪDŽIAI (Computer Skills) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Darbo kompiuteriu įgūdžiai</span>
              <div className="label-icon"><Monitor size={22} /></div>
            </div>
            <div className="row-content">
              {hasObjectData(candidate.computer_skills) ? (
                <div className="computer-skills-grid">
                  {Object.keys(candidate.computer_skills).map((category: string) => (
                    candidate.computer_skills[category] && candidate.computer_skills[category].length > 0 && (
                      <div key={category} className="skill-category">
                        <div className="category-title">{category}</div>
                        <div className="category-skills">
                          {candidate.computer_skills[category].map((skill: any, index: number) => (
                            <div key={index} className="comp-skill-row">
                              <span className="skill-name">{skill.name}</span>
                              <div className="skill-bar-wrapper">
                                <div className="skill-bar" data-level={skill.level}></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 6. VAIRAVIMO ĮGŪDŽIAI (Driving Skills) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Vairavimo įgūdžiai</span>
              <div className="label-icon"><Car size={22} /></div>
            </div>
            <div className="row-content">
              {hasData(candidate.driving_skills) ? (
                <>
                  <div className="driving-table">
                    {candidate.driving_skills.map((skill: any, index: number) => (
                      <div key={index} className="driving-row">
                        <span className="driving-category">{skill.category} kategorija</span>
                        <span className="driving-exp">{skill.experience ? `Patirtis ${skill.experience}` : ''}</span>
                      </div>
                    ))}
                  </div>
                  {candidate.driving_experience && (
                    <div className="driving-experience">Vairavimo stažas: {candidate.driving_experience}</div>
                  )}
                  {candidate.use_own_car && (
                    <div className="own-car-note">
                      <Check size={16} /> Galiu naudoti nuosavą automobilį darbui
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 7. ASMENINĖS SAVYBĖS (Personal Qualities) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Asmeninės savybės</span>
              <div className="label-icon"><User size={22} /></div>
            </div>
            <div className="row-content">
              {candidate.personal_qualities ? (
                <p className="text-content">{candidate.personal_qualities}</p>
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 8. POMĖGIAI, HOBIAI (Hobbies) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Pomėgiai, hobiai</span>
              <div className="label-icon"><Heart size={22} /></div>
            </div>
            <div className="row-content">
              {candidate.hobbies ? (
                <p className="text-content">{candidate.hobbies}</p>
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 9. NARYSTĖ ORGANIZACIJOSE (Organization Memberships) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Narystė organizacijose</span>
              <div className="label-icon"><Users size={22} /></div>
            </div>
            <div className="row-content">
              {hasData(candidate.organization_memberships) ? (
                <div className="org-table">
                  {candidate.organization_memberships!.map((org: any, index: number) => (
                    <div key={index} className="org-entry">
                      <div className="org-row">
                        <span className="org-label">Pavadinimas</span>
                        <span className="org-value">{org.name}</span>
                      </div>
                      <div className="org-row">
                        <span className="org-label">Statusas</span>
                        <span className="org-value">{org.status || '-'}</span>
                      </div>
                      <div className="org-row">
                        <span className="org-label">Laikotarpis</span>
                        <span className="org-value">{org.period || (org.startDate ? `${org.startDate} - ${org.endDate || 'dabar'}` : '-')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 10. REKOMENDACIJOS (Recommendations) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Rekomendacijos</span>
              <div className="label-icon"><Award size={22} /></div>
            </div>
            <div className="row-content">
              {hasData(candidate.recommendations) ? (
                <div className="recommendations-grid">
                  {candidate.recommendations!.map((rec: any, index: number) => (
                    <div key={index} className="rec-card">
                      <div className="rec-name">{rec.name || rec.personName || '-'}</div>
                      <div className="rec-company">{rec.company || '-'}</div>
                      <div className="rec-position">{rec.position || '-'}</div>
                      <div className="rec-phone">{rec.phone || '-'}</div>
                      <div className="rec-email">{rec.email || '-'}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-data">Nėra duomenų</div>
              )}
            </div>
          </div>

          {/* 11. PAGEIDAUJAMAS DARBAS (Desired Job) */}
          <div className="cv-row">
            <div className="row-label">
              <span className="label-text">Pageidaujamas darbas</span>
              <div className="label-icon"><FileText size={22} /></div>
            </div>
            <div className="row-content">
              <div className="job-prefs">
                <div className="pref-row">
                  <span className="pref-label">Darbo vieta</span>
                  <span className="pref-value">{candidate.job_preferences?.workLocation || candidate.job_preferences?.location || candidate.job_preferences?.desiredLocation || '-'}</span>
                </div>
                <div className="pref-row">
                  <span className="pref-label">Darbo sritis</span>
                  <span className="pref-value">{candidate.job_preferences?.workField || candidate.job_preferences?.field || candidate.job_preferences?.desiredWorkField || '-'}</span>
                </div>
                <div className="pref-row">
                  <span className="pref-label">Darbo tipas</span>
                  <span className="pref-value">{candidate.job_preferences?.workType || candidate.job_preferences?.type || candidate.job_preferences?.desiredWorkType || '-'}</span>
                </div>
                <div className="pref-row">
                  <span className="pref-label">Darbo laikas</span>
                  <span className="pref-value">{candidate.job_preferences?.workTime || candidate.job_preferences?.time || candidate.job_preferences?.desiredWorkTime || '-'}</span>
                </div>
                <div className="pref-row">
                  <span className="pref-label">Pareigybių tipas</span>
                  <span className="pref-value">{candidate.job_preferences?.positionType || candidate.job_preferences?.desiredPositionType || '-'}</span>
                </div>
                <div className="pref-row">
                  <span className="pref-label">Pareigos</span>
                  <span className="pref-value">{candidate.job_preferences?.position || candidate.job_preferences?.desiredPosition || '-'}</span>
                </div>
                <div className="pref-row">
                  <span className="pref-label">Atlyginimas (netto)</span>
                  <span className="pref-value">
                    {candidate.job_preferences?.salary ||
                     (candidate.job_preferences?.salaryMin || candidate.job_preferences?.salaryMax
                       ? `nuo ${candidate.job_preferences.salaryMin || '?'} iki ${candidate.job_preferences.salaryMax || '?'} EUR`
                       : '-')}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ==================== EMPLOYER ACTION SECTION ==================== */}
        {user?.user_type === 'employer' && (
          <footer className="employer-action-section">
            <div className="action-card">
              <h3>Įkelti į kandidatų atrankas</h3>
              {invitationLetters.length === 0 ? (
                <div className="no-letters">
                  <p>Jūs neturite sukurtų kvietimo laiškų.</p>
                  <button className="btn-create" onClick={() => navigate('/employer/invitation-letters')}>Sukurti laišką</button>
                </div>
              ) : (
                <div className="action-form">
                  {/* Job picker — only shown when not coming from a specific job page */}
                  {!jobIdFromUrl && employerJobs.length > 0 && (
                    <div className="form-group">
                      <label>Darbo skelbimas</label>
                      <select value={selectedJobId || ''} onChange={(e) => setSelectedJobId(e.target.value ? Number(e.target.value) : null)}>
                        <option value="">Pasirinkite darbo skelbimą</option>
                        {employerJobs.map((job) => (
                          <option key={job.id} value={job.id}>{job.title}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Atrankų aplankas / laiškas</label>
                    <select value={selectedLetterId || ''} onChange={(e) => setSelectedLetterId(Number(e.target.value))}>
                      <option value="">Pasirinkite atrankų aplanką</option>
                      {invitationLetters.map((letter) => (
                        <option key={letter.id} value={letter.id}>{letter.title}</option>
                      ))}
                    </select>
                  </div>
                  <button className="btn-submit" onClick={handleSendInvitation} disabled={sendingInvitation || !selectedLetterId}>
                    <Check size={18} />
                    {sendingInvitation ? 'SIUNČIAMA...' : 'ĮKELTI'}
                  </button>
                </div>
              )}
            </div>
          </footer>
        )}

        {/* Modals */}
        {showMotivationLetterModal && candidate && (
          <ViewMotivationLetterModal userId={candidate.user_id} candidateName={`${candidate.first_name} ${candidate.last_name}`} onClose={() => setShowMotivationLetterModal(false)} />
        )}
        {showSuccessNotification && (
          <SuccessNotification message="Kvietimas išsiųstas sėkmingai!" onClose={() => setShowSuccessNotification(false)} />
        )}
        {showConfirmDialog && candidate && (
          <ConfirmDialog message={`Ar tikrai norite pakviesti ${candidate.first_name} ${candidate.last_name}?`} onConfirm={confirmSendInvitation} onCancel={() => setShowConfirmDialog(false)} />
        )}
        {showAddToSelectionModal && candidate && (
          <AddCandidateToSelectionModal cvId={candidate.id} candidateName={`${candidate.first_name} ${candidate.last_name}`} onClose={() => setShowAddToSelectionModal(false)} onSuccess={() => console.log('Candidate added!')} />
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CandidateDetailPage;
